import React, { useCallback, useEffect, useRef, useState } from "react";
import { createPortal } from "react-dom";
import clsx from "clsx";
import { ArrowUpRight, Check, Circle, Crop, Highlighter, Pencil, Redo2, RotateCw, Square, Type, Undo2, X } from "lucide-react";

/**
 * Image editing for every image attachment in Onerra: crop, rotate, pen / highlighter marks, rectangles, circles,
 * arrows and text. <ImageEditGate /> (mounted once in App) catches any image picked in any file input and opens the
 * editor before the page sees the file — "Attach original" skips it, "Cancel" attaches nothing. Add
 * data-no-image-edit to an <input type="file"> to opt it out.
 */

type Tool = "crop" | "pen" | "highlight" | "rect" | "ellipse" | "arrow" | "text";
type Pt = { x: number; y: number };
type Shape =
  | { kind: "pen" | "highlight"; color: string; width: number; points: Pt[] }
  | { kind: "rect" | "ellipse" | "arrow"; color: string; width: number; a: Pt; b: Pt }
  | { kind: "text"; color: string; size: number; at: Pt; text: string };

const COLORS = ["#ef4444", "#f59e0b", "#22c55e", "#3b82f6", "#a855f7", "#111827", "#ffffff"];
const WIDTHS = [3, 6, 10];
const EDITABLE = /^image\/(png|jpe?g|webp|bmp)$/i;
const MAX_SIDE = 4000; // very large phone photos are scaled down to this on save

function drawShape(ctx: CanvasRenderingContext2D, s: Shape, k: number) {
  ctx.save();
  ctx.lineCap = "round";
  ctx.lineJoin = "round";
  if (s.kind === "text") {
    ctx.fillStyle = s.color;
    ctx.font = `bold ${s.size * k}px system-ui, sans-serif`;
    ctx.textBaseline = "top";
    ctx.lineWidth = Math.max(2, (s.size * k) / 8);
    ctx.strokeStyle = s.color === "#ffffff" ? "#111827" : "#ffffff";
    s.text.split("\n").forEach((line, i) => {
      ctx.strokeText(line, s.at.x * k, (s.at.y + i * s.size * 1.2) * k);
      ctx.fillText(line, s.at.x * k, (s.at.y + i * s.size * 1.2) * k);
    });
    ctx.restore();
    return;
  }
  ctx.strokeStyle = s.color;
  ctx.lineWidth = s.width * k;
  if (s.kind === "pen" || s.kind === "highlight") {
    if (s.kind === "highlight") {
      ctx.globalAlpha = 0.35;
      ctx.lineWidth = s.width * 3 * k;
    }
    ctx.beginPath();
    s.points.forEach((p, i) => (i ? ctx.lineTo(p.x * k, p.y * k) : ctx.moveTo(p.x * k, p.y * k)));
    if (s.points.length === 1) ctx.lineTo(s.points[0].x * k + 0.1, s.points[0].y * k);
    ctx.stroke();
  } else if (s.kind === "rect") {
    ctx.strokeRect(Math.min(s.a.x, s.b.x) * k, Math.min(s.a.y, s.b.y) * k, Math.abs(s.b.x - s.a.x) * k, Math.abs(s.b.y - s.a.y) * k);
  } else if (s.kind === "ellipse") {
    ctx.beginPath();
    ctx.ellipse(((s.a.x + s.b.x) / 2) * k, ((s.a.y + s.b.y) / 2) * k, (Math.abs(s.b.x - s.a.x) / 2) * k, (Math.abs(s.b.y - s.a.y) / 2) * k, 0, 0, Math.PI * 2);
    ctx.stroke();
  } else if (s.kind === "arrow") {
    const ang = Math.atan2(s.b.y - s.a.y, s.b.x - s.a.x);
    const head = Math.max(12, s.width * 4) * k;
    ctx.beginPath();
    ctx.moveTo(s.a.x * k, s.a.y * k);
    ctx.lineTo(s.b.x * k, s.b.y * k);
    ctx.moveTo(s.b.x * k, s.b.y * k);
    ctx.lineTo(s.b.x * k - head * Math.cos(ang - Math.PI / 7), s.b.y * k - head * Math.sin(ang - Math.PI / 7));
    ctx.moveTo(s.b.x * k, s.b.y * k);
    ctx.lineTo(s.b.x * k - head * Math.cos(ang + Math.PI / 7), s.b.y * k - head * Math.sin(ang + Math.PI / 7));
    ctx.stroke();
  }
  ctx.restore();
}

/** Base image + marks flattened onto a new canvas (full resolution). */
function flatten(base: HTMLCanvasElement, shapes: Shape[]) {
  const c = document.createElement("canvas");
  c.width = base.width;
  c.height = base.height;
  const ctx = c.getContext("2d")!;
  ctx.drawImage(base, 0, 0);
  shapes.forEach((s) => drawShape(ctx, s, 1));
  return c;
}

interface Snapshot {
  base: HTMLCanvasElement;
  shapes: Shape[];
}

export function ImageEditor({ file, index, total, onDone }: { file: File; index: number; total: number; onDone: (result: File | "original" | "cancel") => void }) {
  const viewRef = useRef<HTMLCanvasElement>(null);
  const boxRef = useRef<HTMLDivElement>(null);
  const [base, setBase] = useState<HTMLCanvasElement | null>(null);
  const [shapes, setShapes] = useState<Shape[]>([]);
  const [undo, setUndo] = useState<Snapshot[]>([]);
  const [redo, setRedo] = useState<Snapshot[]>([]);
  const [tool, setTool] = useState<Tool>("arrow");
  const [color, setColor] = useState(COLORS[0]);
  const [width, setWidth] = useState(WIDTHS[1]);
  const [draft, setDraft] = useState<Shape | null>(null);
  const [crop, setCrop] = useState<{ a: Pt; b: Pt } | null>(null);
  const [scale, setScale] = useState(1); // display px per image px
  const [failed, setFailed] = useState(false);
  const [saving, setSaving] = useState(false);
  const drag = useRef<Pt | null>(null);

  // load the picked file into the base canvas
  useEffect(() => {
    const url = URL.createObjectURL(file);
    const img = new Image();
    img.onload = () => {
      const k = Math.min(1, MAX_SIDE / Math.max(img.naturalWidth, img.naturalHeight));
      const c = document.createElement("canvas");
      c.width = Math.round(img.naturalWidth * k);
      c.height = Math.round(img.naturalHeight * k);
      c.getContext("2d")!.drawImage(img, 0, 0, c.width, c.height);
      setBase(c);
      URL.revokeObjectURL(url);
    };
    img.onerror = () => {
      setFailed(true);
      URL.revokeObjectURL(url);
    };
    img.src = url;
  }, [file]);

  // fit the view canvas into the available box
  const fit = useCallback(() => {
    if (!base || !boxRef.current) return;
    const { clientWidth, clientHeight } = boxRef.current;
    setScale(Math.min(1, (clientWidth - 16) / base.width, (clientHeight - 16) / base.height));
  }, [base]);
  useEffect(() => {
    fit();
    window.addEventListener("resize", fit);
    return () => window.removeEventListener("resize", fit);
  }, [fit]);

  // redraw
  useEffect(() => {
    const cv = viewRef.current;
    if (!cv || !base) return;
    cv.width = Math.max(1, Math.round(base.width * scale));
    cv.height = Math.max(1, Math.round(base.height * scale));
    const ctx = cv.getContext("2d")!;
    ctx.drawImage(base, 0, 0, cv.width, cv.height);
    [...shapes, ...(draft ? [draft] : [])].forEach((s) => drawShape(ctx, s, scale));
    if (crop) {
      const x = Math.min(crop.a.x, crop.b.x) * scale;
      const y = Math.min(crop.a.y, crop.b.y) * scale;
      const w = Math.abs(crop.b.x - crop.a.x) * scale;
      const h = Math.abs(crop.b.y - crop.a.y) * scale;
      ctx.save();
      ctx.fillStyle = "rgba(15,23,42,0.55)";
      ctx.beginPath();
      ctx.rect(0, 0, cv.width, cv.height);
      ctx.rect(x, y, w, h);
      ctx.fill("evenodd");
      ctx.strokeStyle = "#fff";
      ctx.setLineDash([6, 4]);
      ctx.lineWidth = 2;
      ctx.strokeRect(x, y, w, h);
      ctx.restore();
    }
  }, [base, shapes, draft, crop, scale]);

  const remember = () => {
    if (!base) return;
    setUndo((u) => [...u.slice(-30), { base, shapes }]);
    setRedo([]);
  };
  const toImg = (e: React.PointerEvent<HTMLCanvasElement>): Pt => {
    const r = e.currentTarget.getBoundingClientRect();
    return { x: Math.max(0, Math.min(base!.width, (e.clientX - r.left) / scale)), y: Math.max(0, Math.min(base!.height, (e.clientY - r.top) / scale)) };
  };
  // marks keep the same on-screen thickness whatever the zoom
  const w = width / scale;

  function down(e: React.PointerEvent<HTMLCanvasElement>) {
    if (!base) return;
    e.currentTarget.setPointerCapture(e.pointerId);
    const p = toImg(e);
    drag.current = p;
    if (tool === "crop") return setCrop({ a: p, b: p });
    if (tool === "text") {
      const text = window.prompt("Text to add:");
      drag.current = null;
      if (!text?.trim()) return;
      remember();
      setShapes((s) => [...s, { kind: "text", color, size: (14 + width * 2) / scale, at: p, text: text.trim() }]);
      return;
    }
    if (tool === "pen" || tool === "highlight") setDraft({ kind: tool, color, width: w, points: [p] });
    else setDraft({ kind: tool, color, width: w, a: p, b: p });
  }
  function move(e: React.PointerEvent<HTMLCanvasElement>) {
    if (!drag.current || !base) return;
    const p = toImg(e);
    if (tool === "crop") return setCrop((c) => (c ? { ...c, b: p } : c));
    setDraft((d) => (!d ? d : d.kind === "pen" || d.kind === "highlight" ? { ...d, points: [...d.points, p] } : d.kind === "text" ? d : { ...d, b: p }));
  }
  function up() {
    if (!drag.current) return;
    drag.current = null;
    if (draft) {
      const tiny = (draft.kind === "rect" || draft.kind === "ellipse" || draft.kind === "arrow") && Math.hypot(draft.b.x - draft.a.x, draft.b.y - draft.a.y) < 3 / scale;
      if (!tiny) {
        remember();
        setShapes((s) => [...s, draft]);
      }
      setDraft(null);
    }
  }

  function applyCrop() {
    if (!base || !crop) return;
    const x = Math.round(Math.min(crop.a.x, crop.b.x));
    const y = Math.round(Math.min(crop.a.y, crop.b.y));
    const cw = Math.round(Math.abs(crop.b.x - crop.a.x));
    const ch = Math.round(Math.abs(crop.b.y - crop.a.y));
    if (cw < 5 || ch < 5) return setCrop(null);
    remember();
    const flat = flatten(base, shapes);
    const c = document.createElement("canvas");
    c.width = cw;
    c.height = ch;
    c.getContext("2d")!.drawImage(flat, x, y, cw, ch, 0, 0, cw, ch);
    setBase(c);
    setShapes([]);
    setCrop(null);
    setTool("arrow");
  }
  function rotate() {
    if (!base) return;
    remember();
    const flat = flatten(base, shapes);
    const c = document.createElement("canvas");
    c.width = flat.height;
    c.height = flat.width;
    const ctx = c.getContext("2d")!;
    ctx.translate(c.width, 0);
    ctx.rotate(Math.PI / 2);
    ctx.drawImage(flat, 0, 0);
    setBase(c);
    setShapes([]);
    setCrop(null);
  }
  function doUndo() {
    const last = undo[undo.length - 1];
    if (!last || !base) return;
    setRedo((r) => [...r, { base, shapes }]);
    setUndo((u) => u.slice(0, -1));
    setBase(last.base);
    setShapes(last.shapes);
    setCrop(null);
  }
  function doRedo() {
    const next = redo[redo.length - 1];
    if (!next || !base) return;
    setUndo((u) => [...u, { base, shapes }]);
    setRedo((r) => r.slice(0, -1));
    setBase(next.base);
    setShapes(next.shapes);
  }
  useEffect(() => {
    const key = (e: KeyboardEvent) => {
      if ((e.ctrlKey || e.metaKey) && e.key.toLowerCase() === "z") (e.preventDefault(), e.shiftKey ? doRedo() : doUndo());
      if (e.key === "Escape") onDone("cancel");
    };
    window.addEventListener("keydown", key);
    return () => window.removeEventListener("keydown", key);
  });

  async function save() {
    if (!base) return;
    setSaving(true);
    const flat = flatten(base, shapes);
    const type = /png|webp|bmp/i.test(file.type) ? "image/png" : "image/jpeg";
    const blob: Blob | null = await new Promise((res) => flat.toBlob(res, type, 0.92));
    setSaving(false);
    if (!blob) return onDone("original");
    const name = type === "image/png" ? file.name.replace(/\.(jpe?g|webp|bmp)$/i, ".png") : file.name;
    onDone(new File([blob], name, { type, lastModified: Date.now() }));
  }

  const edited = undo.length > 0;
  const tools: Array<{ key: Tool; label: string; icon: React.ElementType }> = [
    { key: "crop", label: "Crop", icon: Crop },
    { key: "pen", label: "Pen", icon: Pencil },
    { key: "highlight", label: "Highlight", icon: Highlighter },
    { key: "rect", label: "Rectangle", icon: Square },
    { key: "ellipse", label: "Circle", icon: Circle },
    { key: "arrow", label: "Arrow", icon: ArrowUpRight },
    { key: "text", label: "Text", icon: Type },
  ];

  return createPortal(
    <div className="fixed inset-0 z-[1000] flex flex-col bg-slate-900/95 text-white" role="dialog" aria-modal="true" aria-label="Edit image">
      <div className="flex flex-wrap items-center gap-2 border-b border-white/10 px-3 py-2">
        <div className="mr-auto min-w-0">
          <p className="truncate text-sm font-semibold">Edit image{total > 1 ? ` ${index + 1} of ${total}` : ""}</p>
          <p className="truncate text-xs text-slate-400">{file.name}</p>
        </div>
        <button onClick={() => onDone("cancel")} className="rounded-lg px-3 py-1.5 text-sm text-slate-300 hover:bg-white/10" title="Don't attach this image">
          Cancel
        </button>
        <button onClick={() => onDone("original")} className="rounded-lg border border-white/20 px-3 py-1.5 text-sm hover:bg-white/10">
          Attach original
        </button>
        <button onClick={save} disabled={!base || saving} className="flex items-center gap-1.5 rounded-lg bg-brand-600 px-3 py-1.5 text-sm font-medium hover:bg-brand-500 disabled:opacity-50">
          <Check className="h-4 w-4" /> {edited ? "Save & attach" : "Attach"}
        </button>
      </div>

      <div className="flex flex-wrap items-center gap-1 border-b border-white/10 px-3 py-2">
        {tools.map((t) => (
          <button
            key={t.key}
            onClick={() => (setTool(t.key), t.key !== "crop" && setCrop(null))}
            className={clsx("flex items-center gap-1 rounded-lg px-2.5 py-1.5 text-xs", tool === t.key ? "bg-white text-slate-900" : "text-slate-200 hover:bg-white/10")}
            title={t.label}
          >
            <t.icon className="h-4 w-4" /> <span className="hidden sm:inline">{t.label}</span>
          </button>
        ))}
        <button onClick={rotate} className="flex items-center gap-1 rounded-lg px-2.5 py-1.5 text-xs text-slate-200 hover:bg-white/10" title="Rotate 90°">
          <RotateCw className="h-4 w-4" /> <span className="hidden sm:inline">Rotate</span>
        </button>
        <span className="mx-1 h-6 w-px bg-white/15" />
        {COLORS.map((c) => (
          <button key={c} onClick={() => setColor(c)} className={clsx("h-6 w-6 rounded-full border-2", color === c ? "border-white" : "border-transparent")} style={{ background: c }} aria-label={`Colour ${c}`} />
        ))}
        <span className="mx-1 h-6 w-px bg-white/15" />
        {WIDTHS.map((x) => (
          <button key={x} onClick={() => setWidth(x)} className={clsx("flex h-7 w-7 items-center justify-center rounded-lg", width === x ? "bg-white/20" : "hover:bg-white/10")} aria-label={`Line width ${x}`}>
            <span className="rounded-full bg-white" style={{ width: x + 2, height: x + 2 }} />
          </button>
        ))}
        <span className="mx-1 h-6 w-px bg-white/15" />
        <button onClick={doUndo} disabled={!undo.length} className="rounded-lg p-1.5 hover:bg-white/10 disabled:opacity-30" aria-label="Undo">
          <Undo2 className="h-4 w-4" />
        </button>
        <button onClick={doRedo} disabled={!redo.length} className="rounded-lg p-1.5 hover:bg-white/10 disabled:opacity-30" aria-label="Redo">
          <Redo2 className="h-4 w-4" />
        </button>
        {tool === "crop" && crop && (
          <span className="ml-2 flex gap-1">
            <button onClick={applyCrop} className="flex items-center gap-1 rounded-lg bg-emerald-600 px-2.5 py-1.5 text-xs font-medium hover:bg-emerald-500">
              <Check className="h-3.5 w-3.5" /> Apply crop
            </button>
            <button onClick={() => setCrop(null)} className="rounded-lg px-2 py-1.5 text-xs hover:bg-white/10">
              <X className="h-3.5 w-3.5" />
            </button>
          </span>
        )}
      </div>

      <div ref={boxRef} className="flex min-h-0 flex-1 items-center justify-center overflow-hidden p-2">
        {failed ? (
          <div className="max-w-sm text-center text-sm text-slate-300">
            This image format can't be edited in the browser.
            <div className="mt-3">
              <button onClick={() => onDone("original")} className="rounded-lg bg-brand-600 px-3 py-1.5 text-white">
                Attach original
              </button>
            </div>
          </div>
        ) : !base ? (
          <p className="text-sm text-slate-400">Loading…</p>
        ) : (
          <canvas
            ref={viewRef}
            onPointerDown={down}
            onPointerMove={move}
            onPointerUp={up}
            onPointerCancel={up}
            className={clsx("touch-none rounded shadow-2xl", tool === "text" ? "cursor-text" : "cursor-crosshair")}
          />
        )}
      </div>
      <p className="px-3 pb-2 text-center text-[11px] text-slate-400">
        {tool === "crop" ? "Drag over the part to keep, then Apply crop." : tool === "text" ? "Click where the text should go." : "Drag on the image to draw."} Ctrl+Z undoes.
      </p>
    </div>,
    document.body
  );
}

/**
 * Mounted once (App). Listens — before React does — for files picked in any <input type="file">; when images are
 * among them, it holds the change, runs each image through the editor, puts the results back on the input and lets
 * the change through to the page as usual.
 */
export function ImageEditGate() {
  const [queue, setQueue] = useState<{ input: HTMLInputElement; files: File[]; out: File[]; i: number } | null>(null);
  const passing = useRef(new WeakSet<HTMLInputElement>());

  useEffect(() => {
    const onChange = (e: Event) => {
      const input = e.target as HTMLInputElement;
      if (!(input instanceof HTMLInputElement) || input.type !== "file") return;
      if (passing.current.has(input)) {
        passing.current.delete(input);
        return;
      }
      if (input.hasAttribute("data-no-image-edit")) return;
      const files = Array.from(input.files ?? []);
      if (!files.some((f) => EDITABLE.test(f.type))) return;
      e.stopImmediatePropagation();
      e.stopPropagation();
      setQueue({ input, files, out: [], i: 0 });
    };
    window.addEventListener("change", onChange, true);
    return () => window.removeEventListener("change", onChange, true);
  }, []);

  // skip over the non-image files in the batch
  useEffect(() => {
    if (!queue) return;
    if (queue.i >= queue.files.length) return finish(queue.input, queue.out);
    const f = queue.files[queue.i];
    if (!EDITABLE.test(f.type)) setQueue({ ...queue, out: [...queue.out, f], i: queue.i + 1 });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [queue]);

  function finish(input: HTMLInputElement, out: File[]) {
    setQueue(null);
    if (!out.length) {
      input.value = "";
      return;
    }
    const dt = new DataTransfer();
    out.forEach((f) => dt.items.add(f));
    input.files = dt.files;
    passing.current.add(input);
    input.dispatchEvent(new Event("change", { bubbles: true }));
  }

  if (!queue || queue.i >= queue.files.length) return null;
  const current = queue.files[queue.i];
  if (!EDITABLE.test(current.type)) return null;
  const imageCount = queue.files.filter((f) => EDITABLE.test(f.type)).length;
  const imageIndex = queue.files.slice(0, queue.i).filter((f) => EDITABLE.test(f.type)).length;
  return (
    <ImageEditor
      key={`${queue.i}-${current.name}`}
      file={current}
      index={imageIndex}
      total={imageCount}
      onDone={(r) => setQueue((q) => (q ? { ...q, out: r === "cancel" ? q.out : [...q.out, r === "original" ? current : r], i: q.i + 1 } : q))}
    />
  );
}
